/** @file readHeader: parses and validates an IHDR payload. */
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import { IMAGE_IO_ERRORS } from '../errors/imageIoErrors';
import type { PngHeader } from './pngHeader';

/** Legal bit depths per colour type (PNG §11.2.2). */
const DEPTHS: Record<number, readonly number[]> = { 0: [1, 2, 4, 8, 16], 2: [8, 16], 3: [1, 2, 4, 8], 4: [8, 16], 6: [8, 16] };
const PATH = { path: '/png/IHDR' };

/** The header; `PNG_COLOR_TYPE`, `PNG_BIT_DEPTH`, `PNG_FILTER` (method ≠ 0), `PNG_INFLATE` (compression ≠ 0) at `/png/IHDR`. */
export function readHeader(ihdr: Uint8Array): PngHeader {
  if (ihdr.length !== 13) return throwError(IMAGE_IO_ERRORS.PNG_TRUNCATED, { length: ihdr.length }, PATH);
  const dv = new DataView(ihdr.buffer, ihdr.byteOffset, 13);
  const [bitDepth, colorType, compression, filter, interlace] = ihdr.subarray(8, 13);
  const depths = DEPTHS[colorType ?? -1];
  if (depths === undefined) return throwError(IMAGE_IO_ERRORS.PNG_COLOR_TYPE, { colorType }, PATH);
  if (!depths.includes(bitDepth ?? -1)) return throwError(IMAGE_IO_ERRORS.PNG_BIT_DEPTH, { bitDepth, colorType }, PATH);
  if (compression !== 0) return throwError(IMAGE_IO_ERRORS.PNG_INFLATE, { compression }, PATH);
  if (filter !== 0) return throwError(IMAGE_IO_ERRORS.PNG_FILTER, { method: filter }, PATH);
  if (interlace !== 0 && interlace !== 1) return throwError(IMAGE_IO_ERRORS.PNG_INTERLACE_UNSUPPORTED, { interlace }, PATH);
  return {
    width: dv.getUint32(0), height: dv.getUint32(4),
    bitDepth: bitDepth as PngHeader['bitDepth'], colorType: colorType as PngHeader['colorType'], interlace,
  };
}
